import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Card, CardContent, Typography, Stack, Chip, LinearProgress, Button, Grid, Alert,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import axiosInstance from '../utils/axios';

export default function RiderResultsCenter() {
  const [data, setData] = useState(null);
  const [division, setDivision] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const [dash, summary] = await Promise.all([
          axiosInstance.get('/me/dashboard'),
          axiosInstance.get('/me/division/summary').catch(() => ({ data: null })),
        ]);
        if (!cancelled) {
          setData(dash.data);
          setDivision(summary.data);
        }
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.message || 'Failed to load results');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const totals = data?.totals || {};
  const attendance = data?.attendance || {};
  const progress = Array.isArray(data?.progress) ? data.progress : [];
  const completions = Array.isArray(data?.completions) ? data.completions : [];
  const thSnapshot = data?.truckershubStats?.snapshot || {};

  const kpiTotals = useMemo(() => {
    const jobs = Number(thSnapshot.jobs ?? totals.totalJobs ?? totals.jobs ?? 0);
    const distance = Number(
      thSnapshot.statistics?.distance?.total
        ?? thSnapshot.distance
        ?? totals.totalKm
        ?? totals.distanceKm
        ?? totals.km
        ?? 0
    );
    const revenue = Number(
      thSnapshot.statistics?.income?.total
        ?? thSnapshot.income
        ?? totals.totalRevenue
        ?? totals.revenue
        ?? 0
    );
    return { jobs, distance, revenue };
  }, [thSnapshot, totals]);

  const challengeDone = useMemo(
    () => completions.length || progress.filter((p) => p.completed || p.percent >= 100).length,
    [completions, progress]
  );

  if (loading) return <LinearProgress />;
  if (error) return <Alert severity="error">{error}</Alert>;

  return (
    <Box sx={{ maxWidth: 1100, mx: 'auto', px: 3, py: 4 }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} sx={{ mb: 3 }} spacing={1}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Results center</Typography>
          <Typography variant="body2" color="text.secondary">
            Your deliveries, challenges, attendance, and division rank in one place.
          </Typography>
        </Box>
        <Button component={RouterLink} to="/dashboard" variant="outlined" size="small">Back to dashboard</Button>
      </Stack>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        {[
          { label: 'Jobs', value: kpiTotals.jobs },
          { label: 'Distance (km)', value: Math.round(kpiTotals.distance).toLocaleString() },
          { label: 'Revenue', value: Math.round(kpiTotals.revenue).toLocaleString() },
          { label: 'Attendance', value: attendance.totalEventsAttended ?? attendance.total ?? attendance.count ?? 0 },
          { label: 'Challenges done', value: challengeDone },
          { label: 'Division rank', value: division?.me?.rank ? `#${division.me.rank}` : '—' },
        ].map((kpi) => (
          <Grid item xs={6} md={4} lg={2} key={kpi.label}>
            <Card variant="outlined" sx={{ height: '100%' }}>
              <CardContent>
                <Typography variant="caption" color="text.secondary">{kpi.label}</Typography>
                <Typography variant="h6" fontWeight={700}>{kpi.value}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle1" fontWeight={700} gutterBottom>Recent challenge progress</Typography>
              <Stack spacing={1}>
                {progress.slice(0, 6).map((p) => (
                  <Stack key={p._id || p.challengeId} direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2" noWrap sx={{ maxWidth: '70%' }}>
                      {p.challengeName || p.name || 'Challenge'}
                    </Typography>
                    <Chip size="small" label={`${Math.round(p.percent || p.progressPercent || 0)}%`} />
                  </Stack>
                ))}
                {!progress.length && (
                  <Typography variant="body2" color="text.secondary">No challenge progress yet.</Typography>
                )}
              </Stack>
              <Button component={RouterLink} to="/challenges" size="small" sx={{ mt: 2 }}>Browse challenges</Button>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle1" fontWeight={700} gutterBottom>Division snapshot</Typography>
              {division?.division ? (
                <Stack spacing={1}>
                  <Typography variant="body1" fontWeight={600}>{division.division.name}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Jobs {division.me?.stats?.totalJobs || 0} · Revenue {Math.round(division.me?.stats?.totalRevenue || 0).toLocaleString()}
                  </Typography>
                  <Button component={RouterLink} to="/division" size="small">Open my division</Button>
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary">You are not in a division yet.</Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}
